import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

import { cn } from "@/shared/lib";

import { useAssetLookup } from "../../api/useAssetLookup";
import { formatGameTime } from "../../format/formatGameTime";
import { formatPickRate } from "../../format/formatPickRate";
import type { CoreBuildItem } from "../../model/Analysis";
import { PURCHASE_VERDICT_TONES } from "../../model/PURCHASE_VERDICT_TONES";
import type { Tone } from "../../model/Tone";
import { AssetIcon } from "../AssetIcon";

const CAPTION = msg({
  id: "analysis.core.caption",
  message: "Key items: when they are usually bought and when you did",
});
const ITEM = msg({ id: "analysis.core.item", message: "Item" });
const USUAL = msg({ id: "analysis.core.usual", message: "Usual" });
const MINE = msg({ id: "analysis.core.mine", message: "You" });
const DIFF = msg({ id: "analysis.core.diff", message: "Difference" });
const PICK = msg({ id: "analysis.core.pick", message: "{pick} take it" });
const VIA = msg({ id: "analysis.core.via", message: "bought inside {item}" });
const NOT_BOUGHT = msg({ id: "analysis.core.not_bought", message: "not bought" });
const ON_TIME = msg({ id: "analysis.core.on_time", message: "on time" });
const EARLIER = msg({ id: "analysis.core.earlier", message: "{time} earlier" });

const CHIP_COLORS: Record<Tone, string> = {
  good: cn("border-good/40 bg-good/10 text-good"),
  warn: cn("border-warn/40 bg-warn/10 text-warn"),
  bad: cn("border-bad/40 bg-bad/10 text-bad"),
  neutral: cn("border-border bg-surface-raised text-muted"),
};

// Разница меньше минуты — «вовремя»: среднее время у сильных и так разбросано.
const ON_TIME_S = 60;

type CoreBuildTableProps = {
  items: readonly CoreBuildItem[];
  heroId: number;
};

// Ключевые предметы героя строками: у сильных, у игрока и разница. Цвет разницы — оценка
// покупки с бэкенда (как на таймлайне), сам он не пересчитывается. На телефоне таблица
// прокручивается в своей рамке.
export const CoreBuildTable = ({ items, heroId }: CoreBuildTableProps) => {
  const { _, i18n } = useLingui();
  const findItem = useAssetLookup("items");
  const hero = useAssetLookup("heroes")(heroId);
  if (items.length === 0) {
    return null;
  }
  const delta = (item: CoreBuildItem): string => {
    if (item.bought_at_s === null) {
      return _(NOT_BOUGHT);
    }
    const diff = item.bought_at_s - item.baseline_at_s;
    if (Math.abs(diff) < ON_TIME_S) {
      return _(ON_TIME);
    }
    return diff < 0
      ? _({ ...EARLIER, values: { time: formatGameTime(-diff) } })
      : `+${formatGameTime(diff)}`;
  };
  return (
    <div className="flex flex-col gap-2">
      <p className="text-[13px] text-subtle">{_({ ...CAPTION, values: { hero: hero.name } })}</p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[480px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs text-subtle">
              <th scope="col" className="py-2 font-normal">
                {_(ITEM)}
              </th>
              <th scope="col" className="w-24 py-2 text-right font-normal">
                {_(USUAL)}
              </th>
              <th scope="col" className="w-24 py-2 text-right font-normal">
                {_(MINE)}
              </th>
              <th scope="col" className="w-32 py-2 text-right font-normal">
                {_(DIFF)}
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const asset = findItem(item.item_id);
              const tone: Tone =
                item.verdict === null ? "neutral" : PURCHASE_VERDICT_TONES[item.verdict];
              return (
                <tr
                  key={item.item_id}
                  data-core-item={item.item_id}
                  data-verdict={item.verdict ?? "not_bought"}
                  className="border-b border-border last:border-b-0"
                >
                  <td className="py-2">
                    <span className="flex items-center gap-2.5">
                      <AssetIcon
                        asset={asset}
                        size={36}
                        className={cn("rounded-md", item.bought_at_s === null && "opacity-55")}
                      />
                      <span className="flex min-w-0 flex-col">
                        <span className="font-medium text-foreground">{asset.name}</span>
                        <span className="text-xs text-subtle">
                          {item.via_item_id !== null
                            ? _({ ...VIA, values: { item: findItem(item.via_item_id).name } })
                            : _({
                                ...PICK,
                                values: { pick: formatPickRate(item.pick_rate, i18n.locale) },
                              })}
                        </span>
                      </span>
                    </span>
                  </td>
                  <td className="py-2 text-right font-mono text-muted">
                    {formatGameTime(item.baseline_at_s)}
                  </td>
                  <td className="py-2 text-right font-mono text-foreground">
                    {item.bought_at_s === null ? "—" : formatGameTime(item.bought_at_s)}
                  </td>
                  <td className="py-2 text-right">
                    <span
                      className={cn(
                        "inline-flex rounded-full border px-2.5 py-0.5 text-xs",
                        CHIP_COLORS[tone],
                      )}
                    >
                      {delta(item)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
